import type { Session, Ticker } from '@benzinga/session';
import { QuotesManager, type Logo } from '@benzinga/quotes-manager';
import { CryptoManager, getCryptoLogo, getCryptoLogoMap, isUSDCryptoSymbol } from '@benzinga/crypto-manager';

type EnrichableTicker = Pick<Ticker, 'symbol'> & Partial<Ticker>;

export const enrichTrendingTickers = async (tickers: EnrichableTicker[], session: Session): Promise<Ticker[]> => {
  const tickerSymbols = tickers.map(ticker => ticker.symbol).filter(Boolean);
  if (tickerSymbols.length === 0) return [];

  const quotesManager = session.getManager(QuotesManager);
  const missingLogoSymbols = tickerSymbols.filter((_symbol, index) => !tickers[index]?.logo);

  const [quotesRes, logos] = await Promise.all([
    quotesManager.getDelayedQuotes(tickerSymbols).catch(() => ({ ok: {} as Record<string, Partial<Ticker>> })),
    missingLogoSymbols.length > 0
      ? quotesManager.getQuotesLogosMapped(missingLogoSymbols, {
          fields: 'mark_composite_light,mark_composite_dark',
          search_keys_type: 'symbol',
        })
      : Promise.resolve({ ok: {} as Record<string, Logo> }),
  ]);
  const quotesMap = quotesRes?.ok ?? {};
  const logoMap = logos?.ok ?? {};

  let enriched: Ticker[] = tickers.map(ticker => {
    const quote = quotesMap[ticker.symbol] ?? {};
    const file = logoMap[ticker.symbol]?.files;
    const logo = ticker.logo ?? file?.mark_composite_light ?? file?.mark_composite_dark ?? null;
    return { ...ticker, ...quote, logo, symbol: ticker.symbol };
  });

  const needsCrypto = enriched.filter(ticker => !ticker.logo && isUSDCryptoSymbol(ticker.symbol));
  if (needsCrypto.length > 0) {
    const cryptoMap = await getCryptoLogoMap(
      session.getManager(CryptoManager),
      needsCrypto.map(ticker => ticker.symbol),
    ).catch(() => ({}));

    enriched = enriched.map(ticker => {
      if (ticker.logo || !isUSDCryptoSymbol(ticker.symbol)) return ticker;
      const logo = getCryptoLogo(cryptoMap, ticker.symbol);
      return logo ? { ...ticker, logo } : ticker;
    });
  }

  return enriched;
};
